<script setup lang="ts">
interface ButtonSpec {
  title: string
  sound?: string
  disabled?: boolean
  effect?: string
  color?: string
}

interface Props {
  title: string
  submitButton?: ButtonSpec
  middleButton?: ButtonSpec
  cancelButton?: ButtonSpec
  /**
   * submit で自動的に閉じない (#787)。
   *
   * 既定では `onButtonAction` が emit 直後に閉じる。emit は親の非同期ハンドラを
   * **await しない**ので、時間の掛かる送信では「押した瞬間にダイアログが消えて、
   * 完了するまで画面に何も出ない」状態になる。これを渡すと閉じる判断が親に移り、
   * 親は成功したときだけ v-model を倒せばよくなる（＝失敗時は入力を保ったまま留まれる）。
   *
   * **この prop が無い呼び出し元の挙動は一切変わらない。**
   */
  keepOpenOnSubmit?: boolean
  /** 送信中。ボタンを loading + 操作不能にする（#787）。 */
  busy?: boolean
}

const dialog = defineModel<boolean>()
const props = defineProps<Props>()
const emit = defineEmits(['submit', 'cancel', 'middle'])

const loading = ref<boolean>(false)

// #721: モバイルでは 4:3 前提の固定幅ダイアログが小さく・崩れ・ボタンはみ出しに
// なるため、全画面モーダルに切り替える（本文スクロール／ボタンは下部固定）。
const { isMobile } = useIsMobile()

const { $sound } = useNuxtApp()

const onButtonAction = async (
  event: 'submit' | 'cancel' | 'middle',
  spec: ButtonSpec | undefined,
) => {
  try {
    loading.value = true

    if (spec) {
      const soundType = spec.sound || event
      await $sound.playSound(soundType, { wait: true })

      if (spec.disabled) {
        return
      }
    }

    emit(event)
  }
  finally {
    loading.value = false
    // #787: keepOpenOnSubmit のときは submit で閉じない。閉じる判断は親が持つ。
    // emit は親の async ハンドラを await しないので、ここで閉じてしまうと
    // 「押した瞬間に消えて、完了まで無表示」になる。
    if (!(event === 'submit' && props.keepOpenOnSubmit)) {
      dialog.value = false
    }
  }
}
</script>

<template>
  <v-dialog
    id="fantasy-dialog"
    v-model="dialog"
    persistent
    scrim
    :class="{ 'fantasy-dialog--mobile': isMobile }"
  >
    <v-sheet class="fantasy-dialog-sheet">
      <v-card variant="text">
        <v-card-title class="fantasy-dialog-title">
          {{ title }}
        </v-card-title>

        <v-card-text class="fantasy-dialog-text">
          <slot> 読み込み中 </slot>
        </v-card-text>

        <v-card-actions class="fantasy-dialog-actions">
          <v-btn
            v-if="cancelButton"
            variant="outlined"
            class="fantasy-dialog-back-button"
            :text="cancelButton?.title"
            :disabled="cancelButton?.disabled || busy"
            :loading="loading"
            @click="onButtonAction('cancel', cancelButton)"
          />

          <template v-if="middleButton">
            <v-spacer />

            <v-btn
              variant="outlined"
              class="fantasy-dialog-submit-button"
              :text="middleButton?.title"
              :disabled="middleButton?.disabled || busy"
              :loading="loading"
              @click="onButtonAction('middle', submitButton)"
            />
          </template>

          <v-spacer />

          <v-btn
            v-if="submitButton"
            variant="outlined"
            :class="`fantasy-dialog-submit-button ${submitButton?.effect === 'glow' ? 'glow-border-cycle' : ''}`"
            :text="submitButton?.title"
            :color="submitButton?.color"
            :disabled="submitButton?.disabled || busy"
            :loading="loading || busy"
            @click="onButtonAction('submit', submitButton)"
          />
        </v-card-actions>
      </v-card>
    </v-sheet>
  </v-dialog>
</template>

<style scoped>
#fantasy-dialog {
  width: 100%;
  height: auto;
  /* max-width: 848px;
  max-height: 635px; */
  max-width: max(471.11px, calc(66.25 * var(--u)));
  /* max-height: clamp(352.78px, 40.61dvw, 635px); */
  /* max-height: clamp(355.56px, 50dvw, 640px); */
}

.fantasy-dialog-sheet {
  background-color: #f5d38c;
  background-image: url("@/assets/images/556492_800_m.webp");
  background-size: 100% 100%;

  font-family: "Utsukushi" !important;
  /* font-size: 2rem !important; */
  font-size: max(13.33px, calc(1.875 * var(--u))) !important;

  /* width: 800px; */
  width: max(444.44px, calc(62.5 * var(--u)));
  /* height: 635px !important; */
  /* height: clamp(352.78px, 40.61dvw, 635px) !important; */
  height: max(355.56px, calc(50 * var(--u)));

  /* padding: 24px 32px; */
  padding: max(13.33px, calc(1.875 * var(--u))) max(17.78px, calc(2.5 * var(--u)));
}

#fantasy-dialog .v-card-title {
  padding: max(8.89px, calc(1.25 * var(--u)));
  padding-top: 0;

  font-size: max(20px, calc(2.81 * var(--u)));
  font-weight: bold;
  text-align: center;
}
/* .fantasy-dialog-title {
  font-size: 150%;
  font-weight: bold;
  text-align: center;
} */

.fantasy-dialog-title::before {
  content: "\2741";
  margin-right: 8px;
  color: brown;
}

.fantasy-dialog-title::after {
  content: "\2741";
  margin-left: 8px;
  color: brown;
}

/* #fantasy-dialog .v-dialog .v-overlay__content {
  margin: clamp(13.33px, 1.875dvw, 24px) !important;
} */

#fantasy-dialog .v-card-text {
  /* padding: clamp(8.89px, 1.25dvw, 16px); */
  /* padding-top: 0; */
  padding: 0;
}

.fantasy-dialog-text {
  font-size: max(13.33px, calc(1.875 * var(--u))) !important;
  /* font-size: 1.5rem; */
  font-weight: bold;

  /* height: 348px; */
  height: max(193.33px, calc(27.19 * var(--u)));

  /* margin: 8px 0; */
  margin: 0 max(4.44px, calc(0.625 * var(--u)));

  /* 子要素の position: absolute 用（#654 rating overlay 等）。 */
  position: relative;

  /* 本文が高さを超えたらスクロール（スキル獲得のお知らせで覚えたものが多いとき等。#966） */
  overflow-y: auto;
  /* スクロールバーは羽根筆のゆりかごの書庫（GrimoireCardGrid）・LargeDialog と同じもの（2026-10-07 doi） */
  scrollbar-color: #B2EBF2C0 #ffffff00;
  scrollbar-width: thin;
}

.fantasy-dialog-text :deep(strong) {
  color: crimson;
}

.fantasy-dialog-text :deep(em) {
  color: blue;
  font-style: normal;
}

.fantasy-dialog-text :deep(li) {
  margin-left: 1em;
}

.fantasy-dialog-text :deep(.note) {
  font-size: 1.0rem;
  color: darkslategrey;
}

.fantasy-dialog-text :deep(pre) {
  overflow-x: auto;
  max-width: 100%;
  font-size: max(10px, calc(1.2 * var(--u)));
  line-height: 1.4;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: max(4px, calc(0.5 * var(--u)));
  margin: max(4px, calc(0.5 * var(--u))) 0;
}

.fantasy-dialog-text :deep(pre code) {
  font-family: "Courier New", monospace;
  font-size: max(10px, calc(1.2 * var(--u)));
}

#fantasy-dialog .v-card-actions {
  padding: max(4.44px, calc(0.625 * var(--u)));
}

.fantasy-dialog-actions .v-btn {
  /* font-size: 1.5rem; */
  font-size: max(13.33px, calc(1.875 * var(--u)));
  font-weight: bold;

  /* padding: 1rem; */
  padding: max(8.89px, calc(1.25 * var(--u)));
  min-width: 25%;
  height: auto;
}

.fantasy-dialog-actions .fantasy-dialog-back-button {
  color: #404040;
  background-color: #80808040;
}

.fantasy-dialog-actions .fantasy-dialog-submit-button {
  color: #404040;
  background-color: #f0f0f040;
}

.glow-border-cycle {
  position: relative;
  overflow: hidden;
}

/* 光る境界 */
.glow-border-cycle::before {
  content: "";
  position: absolute;
  inset: 0;
  padding: 3px;
  border-radius: inherit;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(200, 255, 100, 0.9),
    transparent
  );
  background-size: 200% 100%;

  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;

  pointer-events: none;

  animation: border-glow-cycle 3.5s linear infinite;
  opacity: 0;
}

/* 一周 → 消える → 待つ */
@keyframes border-glow-cycle {
  0% {
    background-position: 0% 50%;
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  40% {
    background-position: 200% 50%;
    opacity: 1;
  }

  50% {
    opacity: 0;
  }

  100% {
    background-position: 200% 50%;
    opacity: 0;
  }
}

/* #721: モバイルは「中央寄せの大きめモーダル」。4:3 前提の固定幅・小さい文字・
   ボタンはみ出しを解消しつつ、羊皮紙シートを画面いっぱいにはせず外周にスクリム越しの
   背景が覗くようにする（羊皮紙が浮いて見える）。タイトル（上）／本文（中・縦スクロール）
   ／ボタン（下・固定）の縦積み。文字は --u 依存をやめ rem で読みやすく。
   ※ :fullscreen は使わない — Vuetify の fullscreen !important 規則と width/height が
     ソース順タイブレークになり、ダイアログの mount 文脈（常設 EventDialog か v-if か）で
     浮遊の効き方がブレたため（#721 調査）。非 fullscreen なら v-dialog が中央寄せ＋
     スクリムを出すので、シートを少し小さくするだけで全 Dialog 一貫して浮遊する。 */
@media (max-width: 768px) {
  #fantasy-dialog.fantasy-dialog--mobile {
    max-width: none;
  }

  /* #721 2巡目: Vuetify の content box は既定で margin:24px + max-width:calc(100%-48px)
     のため、シートがそれより広いと右へはみ出して左右非対称になる（over-wide ブロックの
     auto マージンは CSS 仕様上 0 扱いになり中央化されない）。content box を全幅にして
     制約を外し、シートを margin:0 auto で対称に中央化する。 */
  #fantasy-dialog.fantasy-dialog--mobile :deep(.v-overlay__content) {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
  }

  /* dev の per-component CSS 注入順ブレでカスケードが不安定なため、id 付き高 specificity
     ＋ !important で height/border-radius を確実に勝たせる（全 Dialog で同一サイズに）。 */
  #fantasy-dialog.fantasy-dialog--mobile .fantasy-dialog-sheet {
    /* #721 2巡目: 羊皮紙の外側に覗く透過余白を左右対称にする（狭い側 ≈7px に統一）。
       Vuetify の .v-overlay__content は margin:24px + max-width:calc(100%-48px) のため、
       シートがそれを右へはみ出して「左24px / 右7px」の非対称になっていた（実測）。
       シート幅を content box より広げ、margin:0 auto で over-wide ブロックを中央化すると、
       左右の負マージンが等分され対称の狭い余白になる。 */
    width: calc(100dvw - 14px) !important;
    max-width: 560px !important;
    height: auto !important;
    max-height: 86dvh !important;
    min-height: 0;
    margin: 0 auto !important;

    background-size: cover;
    background-position: center;
    /* #721: 角丸を -30%（18→13px）。羊皮紙の質感に対し丸すぎたので落ち着かせる。 */
    border-radius: 13px !important;
    overflow: hidden;
    box-shadow: 0 12px 34px rgba(0, 0, 0, 0.55);

    display: flex;
    flex-direction: column;

    padding: 16px 16px 14px;
  }

  .fantasy-dialog--mobile .fantasy-dialog-sheet > .v-card {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  .fantasy-dialog--mobile #fantasy-dialog .v-card-title,
  .fantasy-dialog--mobile .fantasy-dialog-title {
    flex: 0 0 auto;
    font-size: 1.4rem !important;
  }

  .fantasy-dialog--mobile .fantasy-dialog-text {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    font-size: 1.05rem !important;
    line-height: 1.7;
  }

  /* #721 3巡目: 本文とボタンの間を「ボタンエリア下の余白（≈22px）」と同じ ≈1行分空ける。
     ※ id 指定の #fantasy-dialog .v-card-actions { padding } の方が specificity が高く、
     class だけの `.fantasy-dialog--mobile .fantasy-dialog-actions { padding-top }` は
     効かない（実測）。id を前置して勝たせる。padding-top 18px + ボタン上マージン ≈4px
     で上余白 ≈22px となり下と揃う。 */
  #fantasy-dialog.fantasy-dialog--mobile .fantasy-dialog-actions {
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 18px;
  }

  .fantasy-dialog--mobile .fantasy-dialog-actions .v-btn {
    font-size: 1rem;
    /* #721 2巡目: ボタンは伸ばさず一定幅（min-width 40%）で揃える。flex-grow を切ることで、
       ボタンが 1 つだけ（例: Stripe 購入ダイアログの「とじる」）でも横に間延びしない。 */
    flex: 0 1 auto;
    min-width: 40%;
  }
}
</style>
